import * as _ from "lodash";
import { ThunkResult } from "../../shared/utilities";
import { BpmnJsCliWrapper } from "../model/BpmnJsCliWrapper";
import { IAction } from "../model/IAction";
import { ElementType, IElementDictionary, TypesWithReturn } from "../model/IProcessData";
import { State } from "../model/State";
export const UPDATE_VARIABLE_SCOPES = "UPDATE_VARIABLE_SCOPES";

export function updateVariableScopes(): ThunkResult<void, State> {
    return (dispatch, getState) => {
        dispatch(updateVariableScopesWithElements(getState().elements));
    };
}

export function updateVariableScopesWithElements(elements: IElementDictionary) {
    const rootScope = BuildVariableScopes(elements);
    const dictionary = {} as IVariableScopeDictionary;
    populateScopeDictionary(rootScope, dictionary);
    return ({ scopeDictionary: dictionary, type: UPDATE_VARIABLE_SCOPES } as AnalyzeVariableScopeAction);
}

export interface AnalyzeVariableScopeAction extends IAction {
    scopeDictionary: IVariableScopeDictionary;
}

export interface IElementVariable {
    elementId: string;
    variableName: string | null;
    parameterKey: string | null;
    elementType: ElementType;
}

export interface VariableScope {
    scopeId: string | null;
    accessibleInheritedVariables: IElementVariable[];
    inheritedVariables: IElementVariable[];
    variables: IElementVariable[];
    inheritedReturnVariables: IElementVariable[];
    accessibleInheritedReturnVariables: IElementVariable[];
    returnVariables: IElementVariable[];
    subScopes: IVariableScopeDictionary;
    missingVariableNames: IElementVariable[];
}

export interface IVariableScopeDictionary {
    [elementId: string]: VariableScope;
}

function BuildVariableScopes(elementDictionary: IElementDictionary): VariableScope {
    const rootScope: VariableScope = {
        scopeId: null,
        variables: [],
        subScopes: {},
        returnVariables: [],
        inheritedVariables: [],
        inheritedReturnVariables: [],
        missingVariableNames: [],
        accessibleInheritedVariables: [],
        accessibleInheritedReturnVariables: []
    };
    const elements = BpmnJsCliWrapper.elements(); // Gets all the element ids in the process

    // gets the root scope of the process.
    // Most probably it's the first element in the list, but let's make sure we get the right element.
    let rootElement: BpmnJsElement | null = null;
    for (const e of elements) {
        const element = BpmnJsCliWrapper.element(e);
        if (element.type === "bpmn:Process") {
            rootElement = element;
            break;
        }
    }
    if (rootElement) { // We can only do scope validation if we find the root.
        const currentScope = rootScope;
        rootScope.scopeId = rootElement.id;
        BuildVariableScope(currentScope, rootElement, elementDictionary);
        // Let all the declared variables and returns cascade down to the childs,
        // so that we can use that to check for duplicated variables.
        CascadeVariableDeclarations(currentScope);
    }
    return rootScope;
}

function CascadeVariableDeclarations(currentScope: VariableScope) {
    for (const child in currentScope.subScopes) {
        // TODO: check which variables and returns are actually declared before the child scope, varaibles declared after should not be accessible
        if (Object.prototype.hasOwnProperty.call(currentScope.subScopes, child)) {
            const subScope = currentScope.subScopes[child];
            subScope.inheritedVariables = currentScope.inheritedVariables.concat(currentScope.variables);
            subScope.inheritedReturnVariables = currentScope.inheritedReturnVariables.concat(currentScope.returnVariables);
            const accessibleReturn = _.clone(currentScope.accessibleInheritedReturnVariables);
            const accessibleVariables = subScope.accessibleInheritedVariables.concat(currentScope.accessibleInheritedVariables);
            const element = BpmnJsCliWrapper.element(child);
            const checkedElements: string[] = [];
            if (element.incoming) {
                for (const incoming of element.incoming) {
                    populateAccessibleReturnsAndVariablesRecursive(incoming.source, currentScope, accessibleReturn, accessibleVariables, checkedElements);
                }
            }
            subScope.accessibleInheritedReturnVariables = accessibleReturn;
            subScope.accessibleInheritedVariables = accessibleVariables;
            CascadeVariableDeclarations(subScope);
        }
    }
}

function populateScopeDictionary(variableScope: VariableScope, dictionary: IVariableScopeDictionary) {
    dictionary[variableScope.scopeId as string] = variableScope;
    for (const c in variableScope.subScopes) {
        if (Object.prototype.hasOwnProperty.call(variableScope.subScopes, c)) {
            populateScopeDictionary(variableScope.subScopes[c], dictionary);
        }
    }
}

function BuildVariableScope(currentScope: VariableScope, currentElement: BpmnJsElement, elementDictionary: IElementDictionary) {
    if (currentElement.children) {
        for (const element of currentElement.children) // First scan all children
        {
            const elementId = element.id;
            if (elementDictionary[elementId]) {
                const elementParameters = elementDictionary[elementId];
                // Gather declared variables:
                switch (elementParameters.type) {
                    case ElementType.SequentialForeach:
                        {
                            // Foreach variables are declared in the foreach scope, make sure it's created:
                            if (!currentScope.subScopes[elementId]) {
                                currentScope.subScopes[elementId] = {
                                    variables: [],
                                    subScopes: {},
                                    returnVariables: [],
                                    inheritedVariables: [],
                                    inheritedReturnVariables: [],
                                    missingVariableNames: [],
                                    scopeId: elementId,
                                    accessibleInheritedVariables: [],
                                    accessibleInheritedReturnVariables: []
                                };
                            }
                            if (elementParameters.parameters["variable"]) {
                                const variableDeclaration = {
                                    elementId: elementParameters.id,
                                    elementType: elementParameters.type,
                                    variableName: elementParameters.parameters["variable"],
                                    parameterKey: "variable"
                                };
                                const indexVariableDeclaration = {
                                    elementId: elementParameters.id,
                                    elementType: elementParameters.type,
                                    variableName: elementParameters.parameters["variable"] + "_index",
                                    parameterKey: "variable"
                                };
                                currentScope.subScopes[elementId].variables.push(variableDeclaration);
                                currentScope.subScopes[elementId].variables.push(indexVariableDeclaration);

                                // while the variable is declared in the scope, it should always be accesible from within.
                                // Therefore also add it to the inherited accesible variables.
                                currentScope.subScopes[elementId].accessibleInheritedVariables.push(variableDeclaration);
                                currentScope.subScopes[elementId].accessibleInheritedVariables.push(indexVariableDeclaration);

                            } else {
                                currentScope.subScopes[elementId].missingVariableNames.push({
                                    elementId: elementParameters.id,
                                    elementType: elementParameters.type,
                                    variableName: null,
                                    parameterKey: "variable"
                                });
                            }

                        }
                        break;
                    case ElementType.Catch:
                        // Actually the catch variable is declared inside the error scope that it's poining to, not to the scope where it's drawn
                        {
                            const outgoingSequenceFlows = element.outgoing?.filter(x => x.type === "bpmn:SequenceFlow") || [];
                            if (elementParameters.parameters["expression"]) {
                                // IF in the target shape:
                                if (outgoingSequenceFlows.length === 1) {
                                    const childScopeId = outgoingSequenceFlows[0].target.id;

                                    if (!currentScope.subScopes[childScopeId]) {
                                        currentScope.subScopes[childScopeId] = {
                                            variables: [],
                                            subScopes: {},
                                            returnVariables: [],
                                            inheritedVariables: [],
                                            inheritedReturnVariables: [],
                                            missingVariableNames: [],
                                            scopeId: childScopeId,
                                            accessibleInheritedVariables: [],
                                            accessibleInheritedReturnVariables: []
                                        };
                                    }
                                    const catchVariableDeclaration = { elementId: elementParameters.id, elementType: elementParameters.type, variableName: elementParameters.parameters["expression"], parameterKey: "expression" } as IElementVariable;
                                    currentScope.subScopes[childScopeId].variables.push(catchVariableDeclaration);
                                    // Same thing applies here as for the foreach - the variable is declared in the scope but always accessible from within
                                    currentScope.subScopes[childScopeId].accessibleInheritedVariables.push(catchVariableDeclaration);
                                }
                            } else {
                                currentScope.missingVariableNames.push({ elementId: elementParameters.id, elementType: elementParameters.type, variableName: null, parameterKey: "expression" });
                            }
                        }
                        break;
                    case ElementType.Expression:
                        if (elementParameters.parameters["shouldAssignVariable"]["value"] === "true" || elementParameters.parameters["shouldAssignVariable"]["value"] === true) {
                            if (elementParameters.parameters["variableName"]) {
                                currentScope.variables.push({ elementId: elementParameters.id, elementType: elementParameters.type, variableName: elementParameters.parameters["variableName"], parameterKey: "variableName" });
                            } else {
                                currentScope.missingVariableNames.push({ elementId: elementParameters.id, elementType: elementParameters.type, variableName: null, parameterKey: "variableName" });
                            }
                        }
                        break;
                }
                // Gather returns and assign to scope.
                if (_.indexOf(TypesWithReturn, elementParameters.type) !== -1 && elementParameters.name) {
                    // If the element is not attached to a catch it should be added to the returns.
                    if (!element.incoming?.find((i) => i.source.type === "bpmn:IntermediateCatchEvent")) {
                        currentScope.returnVariables.push({
                            elementId: element.id,
                            elementType: elementParameters.type,
                            variableName: elementParameters.name,
                            parameterKey: null
                        });
                    }
                }
            }
        }
    }

    if (currentElement.children) {
        for (const element of currentElement.children) // Build child scopes:
        {
            const elementId = element.id;
            const elementParameters = elementDictionary[elementId];
            if (elementParameters && _.indexOf([
                ElementType.ParallelForeach,
                ElementType.SequentialForeach,
                ElementType.While, ElementType.SubProcess
            ], elementParameters.type) !== -1) {
                const bpmnElement = BpmnJsCliWrapper.element(elementId);
                if (bpmnElement.children && bpmnElement.children.length > 0) {
                    let childScope: VariableScope;
                    // If a catch shape is pointing to the scope, then it's already declared.
                    // Check if the scope has been declared, in that case use that declaration
                    if (currentScope.subScopes[bpmnElement.id]) {
                        childScope = currentScope.subScopes[bpmnElement.id];
                    } else { // Otherwise declare the scope:
                        childScope = {
                            variables: [],
                            subScopes: {},
                            returnVariables: [],
                            inheritedVariables: [],
                            inheritedReturnVariables: [],
                            missingVariableNames: [],
                            scopeId: bpmnElement.id,
                            accessibleInheritedVariables: [],
                            accessibleInheritedReturnVariables: []
                        };
                        currentScope.subScopes[bpmnElement.id] = childScope;
                    }
                    BuildVariableScope(childScope, bpmnElement, elementDictionary);
                }
            }
        }
    }
}

export function populateAccessibleReturnsAndVariables(element: string | BpmnJsElement | undefined, variableScopeDictionary: IVariableScopeDictionary, accesibleReturns: IElementVariable[], accessibleVariables: IElementVariable[]) {
    let bpmnElement: BpmnJsElement | undefined;
    try {
        bpmnElement = _.isString(element) ? BpmnJsCliWrapper.element(element) : element;
    } catch (error) {
        // tslint:disable:no-console
        console.log("Could not find element " + element + ", skipping validation for it.");
        return;
    }

    return populateAccessibleReturnsAndVariablesForElement(bpmnElement, variableScopeDictionary, accesibleReturns, accessibleVariables);
}
export function populateAccessibleReturnsAndVariablesForElement(
    bpmnElement: BpmnJsElement | undefined, variableScopeDictionary: IVariableScopeDictionary,
    accesibleReturns: IElementVariable[], accessibleVariables: IElementVariable[]) {

    const elementId = bpmnElement?.id;
    // In case a bpmnElement is not a scope type but it has a variable scope defined,
    // then it's a non-scopy error handler and the catch variable should be made available.
    // The result from the scope walk can be used for the element itself:
    if (bpmnElement?.type !== "bpmn:SubProcess" && variableScopeDictionary[elementId as string]) {
        const variableScope = variableScopeDictionary[elementId as string];

        for (const variable of variableScope.accessibleInheritedReturnVariables) {
            accesibleReturns.push(variable);
        }
        for (const variable of variableScopeDictionary[elementId as string].accessibleInheritedVariables) {
            accessibleVariables.push(variable);
        }
    } else {
        const variableScope = variableScopeDictionary[bpmnElement?.parent.id  as string];
        const checkedElements: string[] = [];
        if (variableScope) {
            for (const variable of variableScope.accessibleInheritedReturnVariables) {
                accesibleReturns.push(variable);
            }
            for (const variable of variableScope.accessibleInheritedVariables) {
                accessibleVariables.push(variable);
            }
            if (bpmnElement?.incoming) {
                for (const incoming of bpmnElement.incoming) {
                    // Do not validate for Documentation element input
                    if (incoming.type !== "bpmn:DataInputAssociation") {
                        populateAccessibleReturnsAndVariablesRecursive(incoming.source, variableScope, accesibleReturns, accessibleVariables, checkedElements);
                    }
                }
            }
            // This check is for Inclusive condition sequence flows. Other elements that need to have their references validated are not connections.
            // Bpmn connection elements do not have a incoming array as property but instead have a source object that is the same as a element in the incoming array
            if (bpmnElement?.type === "bpmn:SequenceFlow") {
                const bpmnConnectionElement = bpmnElement as BpmnJsConnectionElement;
                populateAccessibleReturnsAndVariablesRecursive(bpmnConnectionElement.source, variableScope, accesibleReturns, accessibleVariables, checkedElements);
            }
        }
    }
}

function populateAccessibleReturnsAndVariablesRecursive(
    element: BpmnJsElement, variableScope: VariableScope,
    accessibleReturns: IElementVariable[], accessibleVariables: IElementVariable[], checkedElements: string[]) {

    if (element.incoming?.find((i) => i.source.type === "bpmn:IntermediateCatchEvent")) {
        // This is an error handler, stop the walk.
    } else {
        const foundReturn = variableScope.returnVariables.find((r) => r.elementId === element.id);
        if (foundReturn) {
            accessibleReturns.push(foundReturn);
        }
        const foundVariable = variableScope.variables.find((v) => v.elementId === element.id);
        if (foundVariable) {
            accessibleVariables.push(foundVariable);
        }
        checkedElements.push(element.id);
        if (element.incoming) {
            for (const incoming of element.incoming) {
                // Do not validate for Documentation element input or loops
                if (incoming.type !== "bpmn:DataInputAssociation" &&
                    checkedElements.indexOf(incoming.source.id) < 0) {
                    populateAccessibleReturnsAndVariablesRecursive(
                        incoming.source, variableScope, accessibleReturns, accessibleVariables, checkedElements);
                }
            }
        }
    }
}
